/**
 * THE NOTARY FLOW'S DECLARATION — and since its page was deleted, that is all
 * it is.
 *
 * accept-notary-invitation-page.tsx is gone: one composed wizard now serves
 * every profession (spec 42ec3c57, slice 3). What survives here is NOT a
 * leftover of it. NOTARY_STEPS is the INDEPENDENT ORACLE the composition
 * guards are anchored on — C-1 holds poder_judicial's requirement declaration
 * against it, and NOTARY_FLOW is derived from it rather than restating it,
 * which is the circularity a probe already caught once on this programme.
 *
 * canLeaveNotaryStep, notaryStepperSteps and restoreNotaryStep are the
 * RECORDED BEHAVIOUR of the wizard this profession walked before the flows
 * converged, exercised by notary-invite-steps.spec.ts. restoreNotaryStep in
 * particular is what restoreComposedStep was proven against, payload for
 * payload, at the moment the composed page took over; deleting it would
 * delete that proof. They are deliberately not called by any page.
 *
 * THE GATES DELEGATE. Each step's gate is tailConcernSatisfied for its
 * concern, so this declaration cannot drift from the rule the composed page
 * applies — it is the same function.
 */

import {
  professionalStepperEntries,
  tailConcernSatisfied,
  type Concern,
  type RequirementKey,
} from "@/pages/auth/professional-invite-steps";

export type NotaryStep = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Everything the gates read. All of it already existed on the page; no step's
 * completion needed new progress state invented for it (spec thesis T-1).
 */
export interface NotaryInviteState {
  validatedNames: { firstName: string; lastName: string } | null;
  phoneVerified: boolean;
  profileImageFile: File | null;
  /** The four fiscal answers handleSubmit refuses without. */
  fiscalRut: string;
  fiscalRazonSocial: string;
  fiscalIssuesCfe: boolean | null;
  password: string;
  confirmPassword: string;
}

export interface NotaryStepDefinition {
  step: NotaryStep;
  /** i18n key for the stepper's label — short, it sits under a 32px circle. */
  labelKey: string;
  /** Satisfied means "this step may be left", nothing more. */
  gate: (state: NotaryInviteState) => boolean;
  /**
   * What this step is ABOUT, independent of how this flow bundles it.
   *
   * Carried here so the composed flow is DERIVED from this list rather than
   * restating it (spec 42ec3c57). A second array repeating these labels would
   * be two sources of truth about one wizard, and the C-3 test — which
   * compares the composition against the flow — could not tell they had
   * drifted, because both would be its own constructs.
   */
  concern: Concern;
  /** What the applicant supplies on this step. Empty for the signup tail. */
  requirements: readonly RequirementKey[];
}

export const NOTARY_STEPS: readonly NotaryStepDefinition[] = [
  {
    step: 1,
    labelKey: "auth.acceptNotaryInvitation.stepIdentity",
    concern: "identity",
    requirements: ["identity.cedula", "identity.credential"],
    gate: (s) => s.validatedNames != null,
  },
  {
    step: 2,
    labelKey: "auth.acceptNotaryInvitation.stepPhone",
    concern: "phone",
    requirements: [],
    gate: (s) => tailConcernSatisfied("phone", s),
  },
  {
    step: 3,
    labelKey: "auth.acceptNotaryInvitation.stepProfile",
    concern: "profile",
    requirements: [],
    gate: (s) => tailConcernSatisfied("profile", s),
  },
  {
    /*
      COVERAGE HAS NO GATE, and that is the decision rather than an omission.
      An empty selection is a legitimate answer — it shows every request
      unranked — and blocking on it would manufacture coverage people do not
      serve. A gate here is failure mode FM-3.
    */
    step: 4,
    labelKey: "auth.acceptNotaryInvitation.stepCoverage",
    concern: "coverage",
    requirements: [],
    gate: (s) => tailConcernSatisfied("coverage", s),
  },
  {
    /*
      The same four refusals handleSubmit already makes, stated once so the
      button can be disabled instead of the applicant being told after the
      fact. handleSubmit keeps its own checks: it is the last line, and a
      backend error still has to land somewhere.
    */
    step: 5,
    labelKey: "auth.acceptNotaryInvitation.stepBilling",
    concern: "billing",
    requirements: [],
    gate: (s) => tailConcernSatisfied("billing", s),
  },
  {
    /*
      THE PASSWORD IS ITS OWN STEP, not the tail of the fiscal one. Choosing
      a credential for the account and declaring a tax identity are different
      questions asked of different parts of a person's life, and the password
      rules list made the billing step the tallest in the flow — which is the
      shape this whole split exists to remove.
    */
    step: 6,
    labelKey: "auth.acceptNotaryInvitation.stepPassword",
    concern: "password",
    requirements: [],
    gate: (s) => tailConcernSatisfied("password", s),
  },
] as const;

export const NOTARY_STEP_COUNT = NOTARY_STEPS.length;

/** The one question the Continue button asks. */
export function canLeaveNotaryStep(
  step: NotaryStep,
  state: NotaryInviteState,
): boolean {
  const definition = NOTARY_STEPS.find((candidate) => candidate.step === step);
  return definition ? definition.gate(state) : false;
}

export interface NotaryStepperEntry {
  label: string;
  complete: boolean;
}

/**
 * The array OnboardingStepper takes, built from the same gates.
 *
 * `complete` is the CONJUNCTION of two things, and both halves earn their
 * place. The gate half is what stops the stepper claiming a step the button
 * refuses to leave. The index half — `step` has moved past this one — is what
 * keeps the marker on the step actually on screen when the applicant walks
 * BACK: OnboardingStepper derives "current" as the first incomplete entry, so
 * without it, returning to the already-verified phone step would light up the
 * photo instead of the phone.
 */
export function notaryStepperSteps(
  translate: (key: string) => string,
  step: NotaryStep,
  state: NotaryInviteState,
): NotaryStepperEntry[] {
  /*
    ONE IMPLEMENTATION, shared with the accountant flow (spec 42ec3c57).
    Identical behaviour: every NOTARY_STEPS entry declares a gate, and the
    shared builder applies `step < current && gate(state)` exactly as this did.
    The accountant's entries declare none, which is its own existing rule — see
    professional-invite-steps.ts.
  */
  return professionalStepperEntries(NOTARY_STEPS, translate, step, state);
}

/** What sessionStorage holds for a half-finished signup. */
export interface NotaryInviteProgress {
  firstName: string;
  lastName: string;
  cedula: string;
  matricula: string;
  /** The furthest step reached. ABSENT on payloads stored before fbe5fd2a. */
  step?: number;
  /** Carried so the phone gate survives a reload; the server re-checks it. */
  phone?: string;
  phoneVerified?: boolean;
}

/**
 * Where a restored applicant lands.
 *
 * TWO CLAMPS, each for a concrete way the flow would otherwise strand someone:
 *
 * - A payload with NO `step` is one this build did not write. It restores to
 *   step 2, which is exactly what it has always meant (control C-3): the
 *   identity is confirmed, carry on from there.
 * - A stored step is honoured only as far as the state that survives a reload
 *   allows. The phone gate needs `phoneVerified`; the photo is a File and
 *   cannot be persisted at all, so step 3 is the ceiling. Restoring past it
 *   would put the applicant at a submit that refuses on a field three screens
 *   back — failure mode FM-1 arriving by the back door.
 */
export function restoreNotaryStep(progress: NotaryInviteProgress): NotaryStep {
  if (progress.step == null) return 2;
  const ceiling: NotaryStep = progress.phoneVerified ? 3 : 2;
  const stored = Math.floor(progress.step);
  if (!Number.isFinite(stored) || stored < 2) return 2;
  return (stored < ceiling ? stored : ceiling) as NotaryStep;
}

